
import { useMemo, useState } from 'react';
import { Route, Switch, useHistory, useLocation } from 'react-router';

import { CareType, MatchingProvidersType } from '@uplift/api';
import { PATIENT_ONBOARDING_ROUTING_CONFIG, ROUTING_CONFIG } from '@uplift/constants';
import { useCurrentuserRefetch } from '@uplift/contexts';
import { useStore } from '@uplift/hooks';
import { Loading } from '@uplift/pages';
import { ProviderMedicalRole } from '@uplift/types';

import { Matches } from '../../components';
import { OnboardingLayout, ProgressBar, YourProviderBanner } from '../components';
import { BookingForEnum } from '../types';
import { SELF_PAY_ID } from '../utils';
import { useOnboardingMachine } from './machine/use-machine';
import {
  AboutYou, Age, AssessmentEntry, AssessmentHasTherapistQuestion,
  AssessmentIsTherapistGoodFitQuestion, AssessmentMedHistoryQuestion, AssessmentQuestion1,
  AssessmentQuestion2, AssessmentQuestion3, AssessmentQuestion4, AssessmentResult, BookingSuccess,
  ConfirmBooking, CreateAccount, Insurance, InsuranceInfo, LevelOfCare, Match, MatchesLoading,
  NotAvailable, PaymentInfo, ProviderExperienceFilter, ProviderFilters, ProviderIdentityFilter,
  Reason, TooYoung, Welcome, Where, Who
} from './steps';
import { PsychInterstitial } from './steps/psych-interstitial';
import { MatchingFlowTypeEnum } from './types';

const fullScreenRoutes: string[] = [
  PATIENT_ONBOARDING_ROUTING_CONFIG.welcome,
  PATIENT_ONBOARDING_ROUTING_CONFIG.matches,
  PATIENT_ONBOARDING_ROUTING_CONFIG.match,
  PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychMatches,
  PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychMatch,
];
const hideProgressBarRoutes: string[] = [
  PATIENT_ONBOARDING_ROUTING_CONFIG.welcome,
  PATIENT_ONBOARDING_ROUTING_CONFIG.matches,
  PATIENT_ONBOARDING_ROUTING_CONFIG.match,
  PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychMatches,
  PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychMatch,
  PATIENT_ONBOARDING_ROUTING_CONFIG.matchesLoading,
];

export const OnboardingPage: React.FC = () => {
  const history = useHistory();
  const { authStore } = useStore();
  const [ isExiting, setIsExiting ] = useState(false);
  const path = useLocation().pathname;
  const { state, send } = useOnboardingMachine({ isExiting });
  const { refetchUser } = useCurrentuserRefetch();
  const isOnboardingCompleted = state.matches('BookingSuccess');

  const onExit = async () => {
    setIsExiting(true);
    localStorage.removeItem('matching');

    if (isOnboardingCompleted) {
      await refetchUser();
      await authStore.fetchAccessTokenByRefreshToken();
      history.push(ROUTING_CONFIG.main);
      return;
    }

    authStore.logout('https://joinuplift.co/');
  };

  const isBookingForSelf = useMemo(() => {
    return [
      BookingForEnum.Myself,
      BookingForEnum.MyselfAndFamily,
      BookingForEnum.MyselfAndPartner
    ].includes(state.context.who);
  }, [ state.context.who ]);

  // if step has back action, then onBack is defined
  const onBack = state.can({ type: 'Back' }) ? () => send({ type: 'Back' }) : undefined;

  const fullScreen = fullScreenRoutes.includes(path);
  const showProgressBar = !hideProgressBarRoutes.includes(path);

  const banner = useMemo(() => {
    return (
      <YourProviderBanner
        providerId={state.context.selectedProviderId}
        appointment={state.context.appointment}
      />
    );
  }, [ state.context.appointment, state.context.selectedProviderId ]);

  if (state.matches('Initial') || state.matches('Idle') || state.matches('BookingBothBookPsych') || isExiting) {
    return <Loading />;
  }

  return (
    <OnboardingLayout
      hasBackButton={state.can({ type: 'Back' })}
      onBack={onBack}
      fullScreen={fullScreen}
      onExit={onExit}
      isOnboardingCompleted={isOnboardingCompleted}
    >
      {showProgressBar && <ProgressBar progressPercentage={state.context.progressPercentage} />}
      <Switch>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.welcome}>
          <Welcome
            onNext={() => send({ type: 'Welcome.Next' })}
          />
        </Route>
        <Route path={[ PATIENT_ONBOARDING_ROUTING_CONFIG.where, PATIENT_ONBOARDING_ROUTING_CONFIG.where1 ]}>
          <Where
            onNext={data => send({ type: 'Where.Next', data })}
            onSwitchToMarketplace={data => send({ type: 'Where.SwitchToMarketplace', data })}
            onNotAvailable={data => send({ type: 'Where.NotAvailable', data })}
            onBack={onBack}
            stateId={state.context.stateId}
            banner={banner}
            matchingFlow={state.context.matchingFlow}
            providerId={state.context.selectedProviderId}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.notAvailable}>
          <NotAvailable
            stateId={state.context.stateId}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.insurance}>
          <Insurance
            onNext={data => send({ type: 'Insurance.Next', data })}
            onBack={onBack}
            insuranceId={state.context.insuranceId}
            stateId={state.context.stateId}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.who}>
          <Who
            onNext={data => send({ type: 'Who.Next', data })}
            onBack={onBack}
            banner={banner}
            providerId={state.context.selectedProviderId}
            matchingFlow={state.context.matchingFlow}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.age}>
          <Age
            onNext={data => send({ type: 'Age.Next', data })}
            onBack={onBack}
            isBookingForSelf={isBookingForSelf}
            banner={banner}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.tooYoung}>
          <TooYoung
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.reason}>
          <Reason
            onNext={data => send({ type: 'Reason.Next', data })}
            onBack={onBack}
            specialtyIds={state.context.specialtyIds}
            isBookingForSelf={isBookingForSelf}
            isRequired={[ BookingForEnum.Myself, BookingForEnum.SomeoneElse ].includes(state.context.who)}
            hideDescription={state.context.matchingFlow !== MatchingFlowTypeEnum.Marketplace}
            banner={banner}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.levelOfCare}>
          <LevelOfCare
            onNext={data => send({ type: 'LevelOfCare.Next', data })}
            onBack={onBack}
            isBookingForSelf={isBookingForSelf}
          />
        </Route>

        {/* Level of Care Assessment START */}
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentEntry} exact>
          <AssessmentEntry
            onNext={() => send({ type: 'AssessmentEntry.Next' })}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentQuestion1}>
          <AssessmentQuestion1
            onNext={data => send({ type: 'AssessmentQuestion.Next', data })}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentQuestion2}>
          <AssessmentQuestion2
            onNext={data => send({ type: 'AssessmentQuestion.Next', data })}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentQuestion3}>
          <AssessmentQuestion3
            onNext={data => send({ type: 'AssessmentQuestion.Next', data })}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentQuestion4}>
          <AssessmentQuestion4
            onNext={data => send({ type: 'AssessmentQuestion.Next', data })}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentMedHistory}>
          <AssessmentMedHistoryQuestion
            onNext={data => send({ type: 'AssessmentYesNoQuestion.Next', data })}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentHasTherapist}>
          <AssessmentHasTherapistQuestion
            onNext={data => send({ type: 'AssessmentYesNoQuestion.Next', data })}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentIsTherapistGoodFit}>
          <AssessmentIsTherapistGoodFitQuestion
            onNext={data => send({ type: 'AssessmentYesNoQuestion.Next', data })}
            onBack={onBack}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.assessmentResult}>
          <AssessmentResult
            onNext={data => send({ type: 'AssessmentResult.Next', data })}
            onBack={onBack}
            onChooseAnother={() => send({ type: 'AssessmentResult.ChooseAnother' })}
            answers={state.context}
          />
        </Route>
        {/* Level of Care Assessment END */}


        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.providerFilters}>
          <ProviderFilters
            onNext={data => send({ type: 'ProviderFilters.Next', data })}
            onBack={onBack}
            providerFilters={state.context.showTheseProviderFilters}
            careType={state.context.careType}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.providerIdentityFilter}>
          <ProviderIdentityFilter
            onNext={data => send({ type: 'ProviderIdentityFilter.Next', data })}
            onBack={onBack}
            identityFilters={state.context.identityFilters}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.providerExperienceFilter}>
          <ProviderExperienceFilter
            onNext={data => send({ type: 'ProviderExperienceFilter.Next', data })}
            onBack={onBack}
            experienceFilters={state.context.experienceFilters}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.createAccount}>
          <CreateAccount
            onNext={data => send({ type: 'CreateAccount.Next', data })}
            onBack={onBack}
            matchingFlow={state.context.matchingFlow}
            matchId={state.context.matchId}
            referrer={state.context.referrer}
            inviteToken={state.context.inviteToken}
            banner={banner}
            providerId={state.context.selectedProviderId}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.matchesLoading}>
          <MatchesLoading
            hasInsurance={state.context.insuranceId !== SELF_PAY_ID}
            careType={state.context.careType}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.matches}>
          <Matches
            onSelectProvider={data => send({ type: 'Matches.SelectProvider', data })}
            onUpdateFilters={data => send({ type: 'Matches.UpdateFilters', data })}
            matchingData={{
              ...state.context,
              availabilityDayOfWeek: state.context.availabilityDayOfWeek || [],
              availabilityTimeOfDay: state.context.availabilityTimeOfDay || [],
              seed: state.context.seed,
              ageId: state.context.age?.id,
              medicalRole: state.context.careType === CareType.psychiatry
                ? ProviderMedicalRole.psychiatrist
                : ProviderMedicalRole.therapist
            }}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.match}>
          <Match
            isSlotUnavailable={state.context.isSlotUnavailable}
            specialtyIds={state.context.specialtyIds}
            selectedProviderId={state.context.selectedProviderId}
            onScheduleSession={data => send({ type: 'Match.ScheduleSession', data })}
            insuranceId={state.context.insuranceId}
            stateId={state.context.stateId}
            matchingPaymentType={state.context.matchingPaymentType}
            identityFilters={state.context.identityFilters}
            experienceFilters={state.context.experienceFilters}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.aboutYou}>
          <AboutYou
            onBack={onBack}
            onNext={() => send({ type: 'AboutYou.Next' })}
            banner={banner}
            stateId={state.context.stateId}
            matchId={state.context.matchId}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.insuranceInfo}>
          <InsuranceInfo
            onBack={onBack}
            onNext={data => send({ type: 'InsuranceInfo.Next', data })}
            isInviteOrDirectFlow={state.context.matchingFlow !== MatchingFlowTypeEnum.Marketplace}
            providerId={state.context.selectedProviderId}
            insuranceId={state.context.insuranceId}
            banner={banner}
          />
        </Route>
        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.paymentInfo}>
          <PaymentInfo
            onBack={onBack}
            onNext={() => send({ type: 'PaymentInfo.Next' })}
            banner={banner}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.confirmBooking}>
          <ConfirmBooking
            matchId={state.context.matchId}
            onBack={onBack}
            onNext={data => send({ type: 'ConfirmBooking.Next', data })}
            onBookingConflict={() => send({ type: 'ConfirmBooking.BookingConflict' })}
            stateId={state.context.stateId}
            insuranceId={
              state.context.matchingPaymentType === MatchingProvidersType.cashpay
                ? null
                : state.context.insuranceId
            }
            appointment={state.context?.appointment}
            providerId={state.context.selectedProviderId}
            careType={state.context.careType}
            termsAccepted={state.context.termsAccepted}
            matchPreferencesData={{
              modalityId: state.context.modalityId,
              ageGroupId: state.context.age?.id,
              visitReasonIds: state.context.specialtyIds,
              insuranceCompanyId: state.context.insuranceId === SELF_PAY_ID
                ? null
                : state.context.insuranceId,
              stateId: state.context.stateId,
              faithIds: state.context.identityFilters?.faithIds,
              ethnicityIds: state.context.identityFilters?.ethnicityIds,
              genderIds: state.context.identityFilters?.genderIds,
              communityExpertise: {
                faithIds: state.context.experienceFilters.faithIds,
                ethnicityIds: state.context.experienceFilters.ethnicityIds,
                generalIds: state.context.experienceFilters.communityIds
              },
              directFlowData: state.context.matchingFlow === MatchingFlowTypeEnum.Direct ? {
                providerId: state.context.selectedProviderId,
                appointment: state.context.appointment.fromDatetime,
                publicProfileUrl: state.context.providerUrl,
              } : null,
            }}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.psychInterstitial}>
          <PsychInterstitial
            onNext={() => send({ type: 'PsychInterstitial.Next' })}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychMatches}>
          <Matches
            onSelectProvider={data => send({ type: 'Matches.SelectProvider', data })}
            onUpdateFilters={data => send({ type: 'Matches.UpdateFilters', data })}
            matchingData={{
              ...state.context,
              availabilityDayOfWeek: [],
              availabilityTimeOfDay: [],
              seed: state.context.seed,
              ageId: state.context.age?.id,
              medicalRole: ProviderMedicalRole.psychiatrist
            }}
            isBookingBoth
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychMatch}>
          <Match
            isSlotUnavailable={state.context.isSlotUnavailable}
            specialtyIds={state.context.specialtyIds}
            selectedProviderId={state.context.bookingBothSelectedProviderId}
            onScheduleSession={data => send({ type: 'Match.ScheduleSession', data })}
            insuranceId={state.context.bookingBothInsuranceId}
            stateId={state.context.stateId}
            matchingPaymentType={state.context.bookingBothMatchingPaymentType}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.bookingBothPsychInsurance}>
          <InsuranceInfo
            onBack={onBack}
            onNext={data => send({ type: 'InsuranceInfo.Next', data })}
            isInviteOrDirectFlow={state.context.matchingFlow !== MatchingFlowTypeEnum.Marketplace}
            providerId={state.context.bookingBothSelectedProviderId}
            insuranceId={state.context.bookingBothInsuranceId}
            banner={(
              <YourProviderBanner
                providerId={state.context.bookingBothSelectedProviderId}
                appointment={state.context.bookingBothAppointment}
              />
            )}
          />
        </Route>

        <Route path={PATIENT_ONBOARDING_ROUTING_CONFIG.bookingSuccess}>
          <BookingSuccess
            onExit={() => localStorage.removeItem('matching')}
            intakeFormSubmissionId={state.context.intakeFormSubmissionId}
          />
        </Route>

      </Switch>
    </OnboardingLayout>
  );
};
